import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiFileText, FiCheckCircle, FiUser, FiXCircle, FiDollarSign, FiClock, FiAlertTriangle } from "react-icons/fi";
import * as api from "../../api/index";


const Mycontract = () => {
  const { id } = useParams(); // This catches the unique URL ID (creatorLinkId or counterpartyLinkId)
  const navigate = useNavigate();

  const [contractData, setContractData] = useState(null);
  const [role, setRole] = useState(""); // "creator" or "counterparty"
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // 1. Fetch real contract data from backend on page load using the unique ID
  useEffect(() => {
    const fetchContract = async () => {
      try {
        setLoading(true);
        const res = await api.getContractByUniqueId(id);
        setContractData(res.data.contract);
        setRole(res.data.role); // Backend identifies if this link belongs to "creator" or "counterparty"
      } catch (err) {
        console.error("Failed to load contract:", err);
        setErrorMessage(err.response?.data?.message || "Invalid or expired contract link.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchContract();
  }, [id]);

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Loading live contract...</p>
        </div>
      </div>
    );
  }

  if (!contractData) {
    return (
      <div className="bg-gray-50 min-h-screen flex items-center justify-center p-6">
        <div className="bg-white p-8 rounded-[2.5rem] shadow-xl text-center space-y-4 max-w-sm w-full border border-gray-100">
          <FiAlertTriangle size={32} className="text-red-500 mx-auto" />
          <h2 className="text-lg font-black text-gray-900">Contract Not Found</h2>
          <p className="text-xs text-gray-500 font-medium">{errorMessage}</p>
        </div>
      </div>
    );
  }

  const currentStatus = contractData.status; // "pending", "active", "rejected"
  const isPending = currentStatus === "pending";

  // 🔒 BUTTON DISABLE RULES:
  // - Disable if action is processing.
  // - Disable if contract is already accepted/rejected (no longer pending).
  // - Disable if YOU are the creator viewing your own link (Creators cannot accept/reject their own pending contract; only the counterparty can act on it).
  const isActionDisabled = isSubmitting || !isPending || role === "creator";

  const handleAcceptClick = () => {
    setShowConfirmModal(true);
  };

  const confirmAccept = async () => {
    setShowConfirmModal(false);
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await api.acceptContract(contractData._id);
      if (res.data.success) {
       
        navigate("/statecontract", {
          state: {
            status: "accepted",
            itemTitle: contractData.itemTitle,
          },
        });
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to accept contract.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("Are you sure you want to reject this contract?")) return;

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const res = await api.rejectContract(contractData._id);
      if (res.data.success) {
          navigate("/statecontract", {
          state: {
            status: "rejected",
            itemTitle: contractData.itemTitle,
          },
        });
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Failed to reject contract.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactSupport = () => {
    alert("Redirecting to OurASAP Support to request contract cancellation.");
  };

  return (
    <div className="bg-gray-50 min-h-screen pb-12 relative">
      {/* 🔝 Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b px-4 py-4 flex items-center justify-center">
        <h1 className="text-lg font-black text-gray-900 tracking-tight">My Contract Details</h1>
      </div>

      <div className="max-w-xl mx-auto px-6 pt-8">
        {errorMessage && (
          <div className="bg-red-50 border-2 border-red-200 text-red-700 p-4 rounded-2xl text-xs font-bold mb-6">
            {errorMessage}
          </div>
        )}

        {/* 🛡️ Status Banner */}
        {currentStatus === "pending" && (
          <div className="bg-teal-900 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-teal-100 overflow-hidden relative">
            <div className="absolute right-[-10%] top-[-20%] w-32 h-32 bg-white/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                 <div className="p-2 bg-teal-800 rounded-xl"><FiClock className="text-teal-100" size={20} /></div>
                 <h2 className="text-sm font-black uppercase tracking-widest">
                   {role === "creator" ? "Awaiting Other Party Response" : "Action Required: Review & Respond"}
                 </h2>
              </div>
              <p className="text-xs font-bold text-teal-50 leading-relaxed">
                {role === "creator"
                  ? `You created this contract. Waiting for ${contractData.otherPartyName} to accept or reject.`
                  : `${contractData.myName} sent you this paid contract. Review terms and Accept or Reject below.`}
              </p>
            </div>
          </div>
        )}

        {currentStatus === "active" && (
          <div className="bg-green-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-green-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-green-700 rounded-xl"><FiCheckCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Contract Active & Accepted</h2>
            </div>
            <p className="text-xs font-medium text-green-50">
              This contract has been fully accepted by both parties and logged securely on OurASAP.
            </p>
          </div>
        )}

        {currentStatus === "rejected" && (
          <div className="bg-red-800 rounded-[2.5rem] p-6 mb-8 text-white shadow-xl shadow-red-100">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-red-700 rounded-xl"><FiXCircle className="text-white" size={20} /></div>
              <h2 className="text-sm font-black uppercase tracking-widest">Contract Rejected</h2>
            </div>
            <p className="text-xs font-medium text-red-50">
              This contract's terms were rejected. You can create a new contract or contact support for help.
            </p>
          </div>
        )}

        {/* 📝 Contract Actions & Details */}
        <div className="space-y-6">
          
          {role === "creator" && isPending && (
            <p className="text-[11px] font-bold text-orange-600 text-center uppercase tracking-wider">
              🔒 You are viewing your own creator link. Only the counterparty can accept/reject.
            </p>
          )}

          <div className="flex gap-4 pt-2">
            <button
              type="button"
              disabled={isActionDisabled}
              onClick={handleReject}
              className="flex-1 py-5 bg-white border-2 border-red-100 hover:bg-red-50 text-red-600 rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiXCircle size={18} />
              Reject
            </button>
            
            <button
              type="button"
              disabled={isActionDisabled}
              onClick={handleAcceptClick}
              className="flex-1 py-5 bg-green-600 hover:bg-green-700 text-white rounded-[1.5rem] font-black uppercase tracking-[0.15em] text-xs transition-all shadow-xl shadow-green-200 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiCheckCircle size={18} />
              Accept
            </button>
          </div>

          {/* Cancel Text / Link */}
          <div className="text-center pt-2">
            <p className="text-xs font-bold text-gray-500">
              Need to cancel this agreement?{" "}
              <button
                type="button"
                onClick={handleContactSupport}
                className="text-orange-600 hover:text-orange-700 underline font-black uppercase tracking-wider text-[11px] ml-1 transition"
              >
                Contact us on WhatsApp to cancel contract
              </button>
            </p>
          </div>

          {/* Deal Terms Card */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiFileText className="text-orange-600" /> Deal Terms
            </h3>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Item / Service Name</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900">
                {contractData.itemTitle}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Agreement Conditions</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 min-h-[90px] whitespace-pre-wrap text-sm">
                {contractData.itemDescription}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Agreed Amount</label>
              <div className="w-full bg-gray-50 border-2 border-gray-100 rounded-2xl py-4 px-5 font-bold text-gray-900 flex items-center gap-2 text-lg text-orange-600">
                <FiDollarSign size={18} />
                {Number(contractData.price).toLocaleString()} ₦
              </div>
            </div>
          </div>

          {/* Parties Involved Card */}
          <div className="bg-white p-8 rounded-[2.5rem] shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6">
            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
              <FiUser className="text-orange-600" /> Parties Involved
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Creator</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.myName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.myEmail}</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1">Other Party</span>
                <p className="font-extrabold text-gray-900 text-sm">{contractData.otherPartyName}</p>
                <p className="text-xs text-gray-500 font-medium truncate mt-0.5">{contractData.otherPartyEmail}</p>
              </div>
            </div>
          </div>
        </div>

        <p className="text-center mt-10 text-[10px] font-black text-gray-300 uppercase tracking-widest">
          Secure Contracts • Instant Enforcement • OurASAP
        </p>
      </div>

      {/* ⚠️ Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-[2.5rem] p-8 max-w-sm w-full shadow-2xl border border-gray-100 text-center space-y-6">
            <div className="w-16 h-16 bg-orange-50 text-orange-600 rounded-3xl mx-auto flex items-center justify-center shadow-inner">
              <FiAlertTriangle size={32} />
            </div>
            
            <div className="space-y-2">
              <h3 className="text-xl font-black text-gray-900">Are you sure?</h3>
              <p className="text-xs font-bold text-gray-500 leading-relaxed">
                By accepting this contract, you agree to bind yourself to the stated terms and conditions for <span className="text-gray-900">{contractData.itemTitle}</span>.
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-black uppercase tracking-widest text-[10px] transition active:scale-95"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAccept}
                className="flex-1 py-4 bg-green-600 hover:bg-green-700 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] transition shadow-lg shadow-green-100 active:scale-95"
              >
                Yes, Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mycontract;